/**
 * Renders the coalescing missed-call toast stack from
 * `missedCallToastsStore`. Non-modal, no focus stealing, no audio.
 *
 * The "Open chat" action dispatches a `missed-call:open-channel` window
 * event that ChatDock listens for to swap its left panel to the channel.
 */
import { useEffect, useState } from 'react';
import { Phone, Video, X, MessageSquare } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  missedCallToastsStore,
  type MissedCallToastEntry,
} from '@/lib/missed-call-toasts';
import { useQuery } from '@tanstack/react-query';

interface UserPreferences {
  showMissedCallPopups?: boolean;
}

export function MissedCallToasts() {
  // useSyncExternalStore would be ideal, but the store snapshot identity
  // changes on every emit so a manual subscription with state mirroring
  // gives us cleaner React semantics for an array.
  const [entries, setEntries] = useState<MissedCallToastEntry[]>(() =>
    missedCallToastsStore.getSnapshot(),
  );

  useEffect(() => {
    return missedCallToastsStore.subscribe(setEntries);
  }, []);

  const { data: preferences } = useQuery<UserPreferences>({
    queryKey: ['/api/user/preferences'],
  });
  const enabled = preferences?.showMissedCallPopups ?? true;

  // When the user disables popups while toasts are visible, clear them so
  // the off-state takes effect immediately without waiting for dismissal.
  useEffect(() => {
    if (!enabled && entries.length > 0) {
      missedCallToastsStore.clear();
    }
  }, [enabled, entries.length]);

  if (!enabled || entries.length === 0) return null;

  return (
    <div
      className="pointer-events-none fixed bottom-4 left-4 z-[9500] flex max-w-[calc(100vw-2rem)] flex-col gap-2"
      data-testid="missed-call-toasts"
      aria-live="polite"
      aria-atomic="false"
    >
      {entries.map(entry => (
        <MissedCallToast key={entry.callerId} entry={entry} />
      ))}
    </div>
  );
}

function MissedCallToast({ entry }: { entry: MissedCallToastEntry }) {
  const ModalityIcon = entry.modality === 'video' ? Video : Phone;
  const label =
    entry.count > 1
      ? `${entry.count} missed calls from ${entry.callerName}`
      : `Missed call from ${entry.callerName}`;

  const handleOpen = () => {
    window.dispatchEvent(
      new CustomEvent('missed-call:open-channel', {
        detail: { channelId: entry.channelId, callerId: entry.callerId },
      }),
    );
    missedCallToastsStore.dismiss(entry.callerId);
  };

  const handleDismiss = () => missedCallToastsStore.dismiss(entry.callerId);

  return (
    <div
      className="pointer-events-auto flex w-80 max-w-full items-center gap-3 rounded-md border border-border/60 bg-background/95 px-3 py-2 shadow-lg backdrop-blur-sm"
      data-testid={`missed-call-toast-${entry.callerId}`}
    >
      <ModalityIcon className="h-4 w-4 text-red-500 shrink-0" />
      <div className="min-w-0 flex-1">
        <div className="text-xs font-medium truncate" title={label}>
          {label}
        </div>
        <div className="text-[10px] text-muted-foreground capitalize">
          {entry.modality}
        </div>
      </div>
      <Button
        size="sm"
        variant="secondary"
        className="h-7 px-2 text-xs gap-1"
        onClick={handleOpen}
        data-testid={`button-open-chat-${entry.callerId}`}
      >
        <MessageSquare className="h-3 w-3" />
        Open chat
      </Button>
      <button
        type="button"
        onClick={handleDismiss}
        className="text-muted-foreground hover:text-foreground p-0.5 rounded"
        title="Dismiss"
        data-testid={`button-dismiss-missed-call-${entry.callerId}`}
      >
        <X className="h-3.5 w-3.5" />
      </button>
    </div>
  );
}
